import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { route } from 'preact-router';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';

import { T } from '../styles/tokens';
interface MyAction {
  id: string;
  name: string;
  description: string | null;
  agent_name: string;
  price: number;
  price_type: 'FREE' | 'PAID';
  is_marketplace: number;
  is_active: number;
  total_executions: number;
  total_revenue: number;
  unique_users: number;
}

const actions = signal<MyAction[]>([]);
const loading = signal(true);
const message = signal<{ type: 'success' | 'error'; text: string } | null>(null);

async function fetchMyActions() {
  loading.value = true;
  try {
    const data = await api.get<{ actions: MyAction[] }>('/marketplace/my-actions?page=1&limit=50');
    actions.value = data.actions;
  } catch {
    actions.value = [];
  } finally {
    loading.value = false;
  }
}

export function MyActionsPage() {
  useEffect(() => { fetchMyActions(); }, []);

  if (loading.value) return (<div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>);

  return (
    <div style={{ padding: '24px', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
 <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Minhas Ações</h1>
          <p style={{ color: T.textMuted }}>Gerencie suas ações publicadas no marketplace</p>
        </div>
        <Button variant="ghost"
          onClick={() => route('/create')}
          style={{
            padding: '12px 24px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #007AFF, #0051D5)',
            color: T.text,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px var(--t-primary-bg)',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
            (e.target as HTMLButtonElement).style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.boxShadow = '0 4px 12px var(--t-primary-bg)';
            (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
          }}
        >
 Criar minha tarefa
        </Button>
      </div>

      {message.value && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: message.value.type === 'success' ? T.successBg : T.dangerBg,
          color: message.value.type === 'success' ? T.success : '#ff3b30',
          fontSize: 14,
        }}>
          {message.value.text}
        </div>
      )}

      {actions.value.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
 <div style={{ fontSize: 48, marginBottom: 16 }}></div>
          <p>Você ainda não criou nenhuma ação</p>
          <p style={{ fontSize: 13, marginTop: 8 }}>Crie agentes e ações para publicar no marketplace</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {actions.value.map(action => (
            <div key={action.id} style={{
              background: T.bgSecondary, borderRadius: 12, padding: 20,
              border: `1px solid ${T.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: 16,
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{action.name}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: action.is_marketplace ? T.successBg : T.borderHover,
                    color: action.is_marketplace ? T.success : T.textMuted,
                  }}>
 {action.is_marketplace ? 'No Marketplace' : 'Privado'}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: action.price_type === 'FREE' ? T.successBg : T.warningBg,
                    color: action.price_type === 'FREE' ? T.success : '#ff9500',
                  }}>
                    {action.price_type === 'FREE' ? 'GRÁTIS' : `R$ ${action.price.toFixed(2)}`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted }}>
                  {action.agent_name} · {action.description || 'Sem descrição'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{action.total_executions}</div>
                  <div style={{ color: T.textMuted, fontSize: 11 }}>Tarefas</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: T.success }}>
                    R$ {action.total_revenue.toFixed(2)}
                  </div>
                  <div style={{ color: T.textMuted, fontSize: 11 }}>Receita</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{action.unique_users}</div>
                  <div style={{ color: T.textMuted, fontSize: 11 }}>Usuários</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
